<template>
  <div data-view="payments">
    <router-view />

    <layout-logged-frame :title="title">
      <template v-slot:content>
        <PaymentsFilters
          @filter="onFilter"
          @clearFilters="onClearFilters"
        />

        <table-base
          :columns="columns"
          :items="payments"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectPayment"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.isCurrency">
              {{
                displayCurrencyColumn(scope.cell.item, scope.cell.column.value)
              }}
            </template>

            <template v-else-if="scope.cell.column.value === 'created_at'">
              {{ scope.cell.item.created_at ? $dayjs(scope.cell.item.created_at).format('MMM D, YYYY') : '-' }}
            </template>

            <template v-else-if="scope.cell.column.isPercent">
              {{
                `${displayColumnValue(
                  scope.cell.item,
                  scope.cell.column.value,
                )}%`
              }}
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import PaymentsFilters from '../../components/forms/filters/Payment';
  import { isStr, extractNestedProp } from '../../utils';
  import columns from '../../config/payments/columns';

  export default {
    components: {
      PaymentsFilters,
    },
    data() {
      return {
        columns,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: 'created_at',
          totalItems: 0,
          search: '',
        },
        totalPages: 0,
        query: { per_page: 15 },
        isLoading: false,
      };
    },
    watch: {
      currentRoute(to, from) {
        if (
          to.name === 'payments' &&
          to.query?.status === 'success' &&
          from.name === 'payments-edit'
        ) {
          this.getPayments(this.query);
        }
      },
    },
    computed: {
      title() {
        const amount = this.totalCount > 0 ? `(${this.totalCount})` : '';
        return this.$t('payments') + ' ' + amount;
      },
      payments() {
        return this.$store.getters['payments/items'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      currentRoute() {
        return this.$route;
      },
    },
    methods: {
      getPayments(query = {}) {
        this.isLoading = true;
        this.pagination.order = query.order;

        return this.$store
          .dispatch('payments/getItems', query)
          .then((res) => {
            const { itemsMeta } = this.$store.getters['payments/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      selectPayment(payment) {
        this.$store.dispatch('payments/setActiveItem', payment);
        this.$router.push({ name: 'payments-edit', params: { id: payment.id } });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;

        this.getPayments(nextQuery);
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;

        this.getPayments(nextQuery);
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          ...filters,
          page: 1,
        };

        this.getPayments(this.query);
      },
      onClearFilters() {
        const { sortBy, order } = this.pagination;
        this.query = { sort: sortBy, order, per_page: 15, page: 1 };
        this.getPayments(this.query);
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
      displayCurrencyColumn(item, columnValue) {
        const value = extractNestedProp(item, columnValue);
        const { symbol } = item.currency;

        return `${value} ${symbol}`;
      },
    },
  };
</script>

<style lang="scss">
@use '../../assets/styles/theme' as *;

[data-view='payments'] {
  .trigger.toggle-menu {
    margin-right: 0;
  }

  .v-datatable {
    min-width: $table-min-width;
  }
}
</style>
